import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicLayout } from "../../components/PublicLayout";
import { api, MXN, formatDateMX } from "../../lib/apiClient";
import { buildWhatsappUrl, orderWhatsappMessage } from "../../lib/whatsapp";
import { Check, MessageCircle, Sparkles, Cake } from "lucide-react";

export default function Confirmation() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const order = state?.order;

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  if (!order) return <Navigate to="/" replace />;

  const wa = buildWhatsappUrl(settings.whatsapp, orderWhatsappMessage(order, settings.auto_message));
  const cartItems = order.customization?.cart_items || [];

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-5 py-14 text-center relative">
        <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}
          className="w-28 h-28 mx-auto rounded-full bg-salvia/20 flex items-center justify-center mb-2 relative">
          <Cake className="w-14 h-14 text-vino" />
          <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-salvia flex items-center justify-center"><Check className="w-5 h-5 text-white" /></div>
        </motion.div>
        <Sparkles className="w-6 h-6 text-dorado mx-auto mb-2" />
        <h1 className="font-serif text-5xl text-vino">¡Gracias!</h1>
        <p className="font-serif text-2xl text-cafe mt-1">Pedido recibido</p>
        <p className="text-cafe/70 mt-3 text-sm">Te contactaremos por WhatsApp para confirmar disponibilidad y datos de pago 💛</p>

        <div className="bg-blancoCalido border border-beige rounded-[28px] p-6 mt-6 text-left shadow-pastel">
          <p className="text-xs text-cafe/50">Folio</p>
          <p className="font-serif text-3xl text-vino mb-3" data-testid="conf-folio">{order.folio}</p>
          <div className="space-y-1.5 text-sm">
            {cartItems.length ? (
              <div className="space-y-1">
                <span className="text-cafe/60">Productos</span>
                {cartItems.map((item, idx) => (
                  <Row key={idx} l={item.product_name} v={`× ${item.quantity}`} />
                ))}
              </div>
            ) : <Row l="Producto" v={`${order.product_name} × ${order.quantity}`} />}
            <Row l="Fecha" v={formatDateMX(order.delivery_date)} />
            <Row l="Hora" v={order.delivery_time || "-"} />
            <Row l="Entrega" v={order.delivery_method === "recoger" ? "Recoger" : order.delivery_method === "envio" ? "Envío" : "Evento"} />
            <Row l="Total estimado" v={order.estimated_price ? MXN(order.estimated_price) : "Por cotización"} bold />
          </div>
        </div>

        <a href={wa} target="_blank" rel="noreferrer" data-testid="conf-whatsapp-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-4 font-medium hover:scale-[1.02] transition-transform">
          <MessageCircle className="w-5 h-5" /> Continuar por WhatsApp
        </a>
        <button onClick={() => navigate("/pedido")} data-testid="conf-otro-btn"
          className="mt-3 w-full border-2 border-vino text-vino rounded-full py-3.5 font-medium hover:bg-vino/5">
          Hacer otro pedido
        </button>
      </div>
    </PublicLayout>
  );
}

const Row = ({ l, v, bold }) => (
  <div className="flex justify-between gap-3">
    <span className="text-cafe/60">{l}</span>
    <span className={bold ? "font-semibold text-vino" : "text-cafe"}>{v}</span>
  </div>
);
